import { useEffect, useState, useCallback, useMemo, type ReactNode } from 'react';
import type { StudentLessonEnrollmentItem } from '@court-sync/shared';
import toast from 'react-hot-toast';
import { academyStudentService } from '@/services/academyStudentService';
import { bookingService } from '@/services/booking';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { BookOpen, Calendar, Loader2 } from 'lucide-react';
import { BookingActionsMenu } from '@/components/bookings/BookingActionsMenu';
import { useSubscriptionCheck } from '@/hooks/useSubscriptionCheck';
import {
  buildCampSessionClusterByPrimaryId,
  getBookingClusterActionIds,
  type CampClusterBooking,
} from '@/utils/campBookingClusters';

function statusVariant(
  s: StudentLessonEnrollmentItem['bookingStatus']
): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (s) {
    case 'confirmed':
      return 'success';
    case 'pending':
      return 'warning';
    case 'completed':
      return 'secondary';
    case 'cancelled':
      return 'destructive';
    default:
      return 'default';
  }
}

function enrollmentToCampClusterBooking(row: StudentLessonEnrollmentItem): CampClusterBooking {
  return {
    id: row.bookingId,
    lessonId: row.lessonId,
    studentId: '',
    termId: row.termId,
    customerId: row.customerId,
    sessionDate: row.sessionDate,
    campBookingType: row.campBookingType,
    status: row.bookingStatus,
    createdAt: new Date(row.createdAt),
  };
}

function EnrollmentTable({
  items,
  emptyMessage,
  showRowActions,
  clusterByPrimaryId,
  lessonTypeById: _lessonTypeById,
  hasValidSubscription,
  subscriptionTooltip,
  onViewBookingDetails,
  onRequestMoveToAnother,
  onBookingUpdated,
}: {
  items: StudentLessonEnrollmentItem[];
  emptyMessage: string;
  showRowActions?: boolean;
  clusterByPrimaryId: Map<string, CampClusterBooking[]>;
  lessonTypeById: Record<string, string | undefined>;
  hasValidSubscription: boolean;
  subscriptionTooltip?: string;
  onViewBookingDetails?: (bookingId: string) => void;
  onRequestMoveToAnother?: (row: StudentLessonEnrollmentItem) => void;
  onBookingUpdated?: () => void;
}) {
  const handleUpdateStatus = async (
    bookingIds: string[],
    status: 'pending' | 'confirmed' | 'completed'
  ) => {
    if (bookingIds.length === 0) return;
    try {
      await Promise.all(bookingIds.map((id) => bookingService.updateBooking(id, { status })));
      toast.success(
        bookingIds.length > 1 ? 'Bookings updated successfully' : 'Booking status updated successfully'
      );
      onBookingUpdated?.();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to update booking status');
    }
  };

  const handleCancelBookings = async (bookingIds: string[]) => {
    if (bookingIds.length === 0) return;
    try {
      await Promise.all(bookingIds.map((id) => bookingService.cancelBooking(id)));
      toast.success(
        bookingIds.length > 1 ? 'Bookings cancelled successfully' : 'Booking cancelled successfully'
      );
      onBookingUpdated?.();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to cancel booking');
    }
  };

  const handleRestoreBookings = async (bookingIds: string[]) => {
    if (bookingIds.length === 0) return;
    const msg =
      bookingIds.length > 1
        ? `Restore these ${bookingIds.length} cancelled enrollments? They will return to confirmed status.`
        : 'Restore this cancelled enrollment? It will return to confirmed status.';
    if (!window.confirm(msg)) return;
    try {
      await Promise.all(bookingIds.map((id) => bookingService.updateBooking(id, { status: 'confirmed' })));
      toast.success(
        bookingIds.length > 1 ? 'Enrollments restored successfully' : 'Enrollment restored successfully'
      );
      onBookingUpdated?.();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to restore enrollment');
    }
  };

  const showActionsColumn = showRowActions && (onViewBookingDetails || onRequestMoveToAnother || onBookingUpdated);

  const visibleItems = useMemo(() => {
    const hiddenIds = new Set<string>();
    for (const cluster of clusterByPrimaryId.values()) {
      for (let i = 1; i < cluster.length; i++) {
        hiddenIds.add(cluster[i].id);
      }
    }
    return items.filter((row) => !hiddenIds.has(row.bookingId));
  }, [items, clusterByPrimaryId]);

  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">{emptyMessage}</p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border border-border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30 text-left">
            <th className="p-3 font-medium">Lesson</th>
            <th className="p-3 font-medium">Type</th>
            <th className="p-3 font-medium">Schedule</th>
            <th className="p-3 font-medium">Term</th>
            <th className="p-3 font-medium">Status</th>
            {showActionsColumn && (
              <th className="p-3 font-medium w-[1%] text-right">Actions</th>
            )}
          </tr>
        </thead>
        <tbody>
          {visibleItems.map((row) => {
            const campBooking = enrollmentToCampClusterBooking(row);
            const clusterIds = getBookingClusterActionIds(campBooking, clusterByPrimaryId);
            return (
              <tr key={row.bookingId} className="border-b border-border/80">
                <td className="p-3 font-medium">{row.lessonName}</td>
                <td className="p-3 capitalize">{row.lessonType}</td>
                <td className="p-3 text-muted-foreground">{row.scheduleSummary ?? '—'}</td>
                <td className="p-3 text-muted-foreground">{row.termName ?? '—'}</td>
                <td className="p-3">
                  <Badge variant={statusVariant(row.bookingStatus)}>{row.bookingStatus}</Badge>
                </td>
                {showActionsColumn && (
                  <td className="p-3 text-right">
                    {row.bookingStatus === 'pending' || row.bookingStatus === 'confirmed' ? (
                      <BookingActionsMenu
                        bookingId={row.bookingId}
                        lessonId={row.lessonId}
                        pendingInCluster={clusterIds.pendingInCluster}
                        confirmedInCluster={clusterIds.confirmedInCluster}
                        completedInCluster={clusterIds.completedInCluster}
                        cancellableIds={clusterIds.cancellableIds}
                        campSessionClusterSize={clusterIds.campSessionCluster?.length}
                        hasValidSubscription={hasValidSubscription}
                        subscriptionTooltip={subscriptionTooltip}
                        onViewDetails={(id) => onViewBookingDetails?.(id)}
                        onUpdateStatus={handleUpdateStatus}
                        onCancelBookings={handleCancelBookings}
                        onMoveToAnother={
                          onRequestMoveToAnother
                            ? () => onRequestMoveToAnother(row)
                            : undefined
                        }
                        ariaLabel={`Actions for ${row.lessonName}`}
                        triggerVariant="ghost"
                        triggerSize="icon"
                      />
                    ) : row.bookingStatus === 'cancelled' ? (
                      <BookingActionsMenu
                        bookingId={row.bookingId}
                        lessonId={row.lessonId}
                        pendingInCluster={[]}
                        confirmedInCluster={[]}
                        completedInCluster={[]}
                        cancelledInCluster={[row.bookingId]}
                        cancellableIds={[]}
                        hasValidSubscription={hasValidSubscription}
                        subscriptionTooltip={subscriptionTooltip}
                        onViewDetails={(id) => onViewBookingDetails?.(id)}
                        onUpdateStatus={handleUpdateStatus}
                        onCancelBookings={handleCancelBookings}
                        onRestoreBookings={handleRestoreBookings}
                        ariaLabel={`Actions for ${row.lessonName}`}
                        triggerVariant="ghost"
                        triggerSize="icon"
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export interface StudentLessonsPanelProps {
  academyId: string;
  studentId: string;
  onRequestMoveToAnother?: (row: StudentLessonEnrollmentItem) => void;
  onViewBookingDetails?: (bookingId: string) => void;
  /** Shown next to the "Currently enrolled" title (e.g. Add to lesson). */
  enrolledSectionActions?: ReactNode;
  /** Increment to reload enrollments from parent after external changes */
  refreshKey?: number;
}

export function StudentLessonsPanel({
  academyId,
  studentId,
  onRequestMoveToAnother,
  onViewBookingDetails,
  enrolledSectionActions,
  refreshKey = 0,
}: StudentLessonsPanelProps) {
  const { hasValidSubscription, tooltipMessage } = useSubscriptionCheck();
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState<StudentLessonEnrollmentItem[]>([]);
  const [history, setHistory] = useState<StudentLessonEnrollmentItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await academyStudentService.getStudentLessons(academyId, studentId);
      setCurrent(data.current);
      setHistory(data.history);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load lessons');
      setCurrent([]);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [academyId, studentId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const lessonTypeById = useMemo(() => {
    const map: Record<string, string | undefined> = {};
    for (const row of [...current, ...history]) {
      map[row.lessonId] = row.lessonType;
    }
    return map;
  }, [current, history]);

  const clusterByPrimaryId = useMemo(() => {
    const bookings = current.map(enrollmentToCampClusterBooking);
    return buildCampSessionClusterByPrimaryId(bookings, lessonTypeById);
  }, [current, lessonTypeById]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="mr-2 h-6 w-6 animate-spin" aria-hidden />
        Loading lessons…
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-destructive">{error}</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1 min-w-0">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary-600 shrink-0" aria-hidden />
                <h3 className="text-lg font-semibold">Currently enrolled</h3>
              </div>
              <p className="text-sm text-muted-foreground">Active bookings (pending or confirmed).</p>
            </div>
            {enrolledSectionActions ? (
              <div className="shrink-0 w-full sm:w-auto">{enrolledSectionActions}</div>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          <EnrollmentTable
            items={current}
            emptyMessage="No active lesson enrollments."
            showRowActions
            clusterByPrimaryId={clusterByPrimaryId}
            lessonTypeById={lessonTypeById}
            hasValidSubscription={hasValidSubscription}
            subscriptionTooltip={tooltipMessage}
            onViewBookingDetails={onViewBookingDetails}
            onRequestMoveToAnother={onRequestMoveToAnother}
            onBookingUpdated={load}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-muted-foreground shrink-0" aria-hidden />
            <h3 className="text-lg font-semibold">History</h3>
          </div>
          <p className="text-sm text-muted-foreground">Completed or cancelled enrollments.</p>
        </CardHeader>
        <CardContent>
          <EnrollmentTable
            items={history}
            emptyMessage="No past enrollments."
            showRowActions
            clusterByPrimaryId={new Map()}
            lessonTypeById={lessonTypeById}
            hasValidSubscription={hasValidSubscription}
            subscriptionTooltip={tooltipMessage}
            onViewBookingDetails={onViewBookingDetails}
            onBookingUpdated={load}
          />
        </CardContent>
      </Card>
    </div>
  );
}
